import React, { useState } from 'react';
import Button from './Button';
import Input from './Input';
import Icon from '../AppIcon';

const AddCharacterModal = ({ isOpen, onClose, onAddCharacter }) => {
  const [characterName, setCharacterName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    e?.stopPropagation(); // Prevent event bubbling to parent form
    
    if (!characterName?.trim()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      await onAddCharacter?.(characterName?.trim());
      setCharacterName('');
      onClose?.();
    } catch (error) {
      console.error('Error adding character:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = (e) => {
    if (e) {
      e?.preventDefault();
      e?.stopPropagation(); // Prevent event bubbling
    }
    
    if (!isSubmitting) {
      setCharacterName('');
      onClose?.();
    }
  };

  const handleModalClick = (e) => {
    // Prevent modal clicks from bubbling to parent form
    e?.stopPropagation();
  };

  const handleBackdropClick = (e) => {
    // Only close if clicking the backdrop itself, not the modal content
    if (e?.target === e?.currentTarget) {
      handleClose(e);
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={handleBackdropClick}
    >
      <div 
        className="bg-white bg-opacity-10 backdrop-blur-md border border-white border-opacity-20 rounded-2xl p-6 w-full max-w-md mx-auto"
        onClick={handleModalClick} // Prevent clicks on modal from bubbling
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-2">
            <Icon name="UserPlus" size={24} className="text-blue-300" />
            <h2 className="text-xl font-semibold text-white">Add New Character</h2>
          </div>
          
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-2 text-gray-400 hover:text-white hover:bg-white hover:bg-opacity-10 rounded-lg transition-colors disabled:opacity-50"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <Input
              label="Character Name"
              type="text"
              value={characterName}
              onChange={(e) => setCharacterName(e?.target?.value)}
              onKeyDown={(e) => {
                if (e?.key === 'Enter') {
                  e?.preventDefault();
                  handleSubmit(e);
                }
              }}
              placeholder="Enter character name (e.g., Elsa, Hamlet, Maria)"
              className="bg-white bg-opacity-10 border-white border-opacity-30 text-white rounded-xl"
              disabled={isSubmitting}
              required
              autoFocus
            />
            <p className="text-blue-200 text-xs mt-2">
              Enter the full name of the character as it appears in the musical
            </p>
          </div>

          <div className="flex items-center space-x-3 pt-4">
            <Button
              type="submit"
              disabled={!characterName?.trim() || isSubmitting}
              onClick={(e) => {
                e?.preventDefault();
                e?.stopPropagation();
                handleSubmit(e);
              }}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-600 disabled:opacity-50 text-white rounded-xl"
              iconName={isSubmitting ? "Loader2" : "Plus"}
              iconPosition="left"
            >
              {isSubmitting ? 'Adding Character...' : 'Add Character'}
            </Button>
            
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
              className="border-white border-opacity-30 text-white hover:bg-white hover:bg-opacity-10 rounded-xl disabled:opacity-50"
            >
              Cancel
            </Button>
          </div>
        </form>

        <div className="mt-6 p-4 bg-blue-500 bg-opacity-20 border border-blue-400 border-opacity-30 rounded-xl">
          <div className="flex items-start space-x-2">
            <Icon name="Info" size={16} className="text-blue-300 mt-0.5 flex-shrink-0" />
            <div>
              <h4 className="text-blue-200 font-medium text-sm mb-1">Character Tips</h4>
              <p className="text-blue-100 text-xs">
                Add all main characters and principal roles. You can always add more characters later or edit character details from the role management section.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddCharacterModal;